import { useState, useMemo, useCallback, useRef } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  Landmark, Plus, RefreshCw, ArrowUpRight, ArrowDownLeft, Search,
  CheckCircle2, Clock, AlertTriangle, Link2, Unlink, Shield,
  TrendingUp, TrendingDown, IndianRupee, Building2, Eye, GitCompare,
  Filter, ChevronDown, ChevronRight, Wifi, WifiOff, CalendarDays,
  Upload, FileText, FileSpreadsheet, BookOpen, Zap, ArrowRight, Trash2
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import * as XLSX from "xlsx";

// ── Types ────────────────────────────────────────────────────────────────

interface BankAccount {
  id: string;
  bankName: string;
  bankCode: string;
  accountNumber: string;
  accountType: "savings" | "current" | "overdraft";
  ifscCode: string;
  branch: string;
  holderName: string;
  balance: number;
  lastSynced: string | null;
  status: "connected" | "disconnected" | "syncing" | "error";
  linkedLedgerId?: string;
  logo: string;
}

interface BankTransaction {
  id: string;
  bankAccountId: string;
  date: string;
  description: string;
  reference: string;
  type: "credit" | "debit";
  amount: number;
  balance: number;
  category?: string;
  reconciliationStatus: "matched" | "unmatched" | "pending" | "ignored";
  matchedVoucherId?: string;
  matchedVoucherNumber?: string;
}

// ── Statement Import Types ───────────────────────────────────────────────

interface ImportedStatement {
  id: string;
  date: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  balance: number;
  type: "credit" | "debit";
  suggestedCategory: string;
  suggestedDebitLedger: string;
  suggestedCreditLedger: string;
  suggestedVoucherType: string;
  confidence: "high" | "medium" | "low";
  status: "pending" | "posted" | "skipped";
}

const CATEGORY_RULES: { pattern: RegExp; category: string; debitLedger: string; creditLedger: string; voucherType: string; confidence: "high" | "medium" }[] = [
  // Salary & Payroll
  { pattern: /salary|salaries|wages|payroll|bonus\s*pay/i, category: "Salary", debitLedger: "Salary Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Rent
  { pattern: /rent|lease|office\s*space|co-?working/i, category: "Rent", debitLedger: "Rent Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Utilities
  { pattern: /electricity|power|energy|bescom|mseb|bses|tata\s*power|water\s*bill/i, category: "Utilities", debitLedger: "Utility Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Telecom & Internet
  { pattern: /airtel|jio|vodafone|bsnl|broadband|internet|mobile\s*bill/i, category: "Telecom", debitLedger: "Utility Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // IT & Cloud
  { pattern: /google\s*(cloud|workspace)|aws|azure|cloud|hosting|digitalocean|heroku/i, category: "IT Services", debitLedger: "Software & Subscriptions", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
  // Software & SaaS
  { pattern: /zoho|tally|slack|notion|figma|github|microsoft|adobe|canva|freshworks/i, category: "Software", debitLedger: "Software & Subscriptions", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
  // Tax
  { pattern: /gst|tax|tds|income\s*tax|advance\s*tax|challan|professional\s*tax/i, category: "Tax Payment", debitLedger: "GST Output (Payable)", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Insurance
  { pattern: /insurance|premium|lic|hdfc\s*life|icici\s*prudential|policy/i, category: "Insurance", debitLedger: "Insurance Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Sales Receipt
  { pattern: /neft.*inv|rtgs.*inv|payment.*inv|receipt.*client|collection/i, category: "Sales Receipt", debitLedger: "Bank Account", creditLedger: "Sales Account", voucherType: "Receipt", confidence: "high" },
  // Purchase
  { pattern: /purchase|supplier|vendor|material|raw\s*material|procurement/i, category: "Purchase", debitLedger: "Purchase Account", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
  // Interest
  { pattern: /interest\s*credit|int\s*cr|fd\s*interest/i, category: "Interest Income", debitLedger: "Bank Account", creditLedger: "Interest Income", voucherType: "Receipt", confidence: "high" },
  // Bank Transfer
  { pattern: /transfer|trf|internal|neft.*self|rtgs.*self/i, category: "Internal Transfer", debitLedger: "Bank Account", creditLedger: "Bank Account", voucherType: "Contra", confidence: "medium" },
  // Food & Delivery — AI categorized vendors
  { pattern: /swiggy|zomato|uber\s*eats|dominos|mcdonalds|kfc|pizza\s*hut|food|catering|canteen|lunch|dinner|restaurant/i, category: "Food Expense", debitLedger: "Food & Entertainment", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Office Supplies — AI categorized vendors
  { pattern: /amazon|flipkart|staples|office\s*depot|stationery|printer|toner|cartridge|paper/i, category: "Office Supplies", debitLedger: "Office Supplies", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Travel & Fuel — AI categorized
  { pattern: /uber|ola|rapido|cab|taxi|fuel|petrol|diesel|hp\s*petrol|bharat\s*petroleum|iocl|indian\s*oil|shell|cng|irctc|makemytrip|goibibo|flight|train|redbus|bus\s*ticket/i, category: "Travel Expense", debitLedger: "Travel Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Courier & Shipping
  { pattern: /delhivery|bluedart|fedex|dtdc|india\s*post|courier|shipping|freight|ecom\s*express/i, category: "Courier & Shipping", debitLedger: "Courier & Shipping", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
  // Professional Fees
  { pattern: /legal|lawyer|advocate|ca\s*fee|audit\s*fee|consultant|advisory|professional\s*fee/i, category: "Professional Fees", debitLedger: "Professional Fees", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Marketing & Ads
  { pattern: /google\s*ads|facebook|meta\s*ads|linkedin|marketing|advertisement|campaign|promotion|branding/i, category: "Marketing", debitLedger: "Marketing Expense", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
  // Loan & EMI
  { pattern: /emi|loan\s*repay|home\s*loan|vehicle\s*loan|personal\s*loan|equated/i, category: "Loan EMI", debitLedger: "Loan Account", creditLedger: "Bank Account", voucherType: "Payment", confidence: "high" },
  // Repairs & Maintenance
  { pattern: /repair|maintenance|amc|servicing|plumber|electrician/i, category: "Repairs & Maintenance", debitLedger: "Repairs & Maintenance", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
  // E-commerce refund
  { pattern: /refund|cashback|reversal/i, category: "Refund", debitLedger: "Bank Account", creditLedger: "Purchase Account", voucherType: "Receipt", confidence: "medium" },
  // ATM / Cash
  { pattern: /atm|cash\s*withdrawal|self\s*withdrawal/i, category: "Cash Withdrawal", debitLedger: "Cash-in-Hand", creditLedger: "Bank Account", voucherType: "Contra", confidence: "high" },
  // Subscription services
  { pattern: /netflix|spotify|hotstar|prime|youtube\s*premium|subscription/i, category: "Subscriptions", debitLedger: "Software & Subscriptions", creditLedger: "Bank Account", voucherType: "Payment", confidence: "medium" },
];

const ALL_CATEGORIES = [
  "Salary", "Rent", "Utilities", "Telecom", "IT Services", "Software", "Tax Payment",
  "Insurance", "Sales Receipt", "Purchase", "Interest Income", "Internal Transfer",
  "Food Expense", "Office Supplies", "Travel Expense", "Courier & Shipping",
  "Professional Fees", "Marketing", "Loan EMI", "Repairs & Maintenance",
  "Refund", "Cash Withdrawal", "Subscriptions", "Income", "Expense", "Other",
];

function categorizeTransaction(desc: string, type: "credit" | "debit"): { category: string; debitLedger: string; creditLedger: string; voucherType: string; confidence: "high" | "medium" | "low" } {
  for (const rule of CATEGORY_RULES) {
    if (rule.pattern.test(desc)) return { ...rule, confidence: rule.confidence };
  }
  return type === "credit"
    ? { category: "Income", debitLedger: "Bank Account", creditLedger: "Sales Account", voucherType: "Receipt", confidence: "low" }
    : { category: "Expense", debitLedger: "Purchase Account", creditLedger: "Bank Account", voucherType: "Payment", confidence: "low" };
}

function parseCSVRows(text: string): string[][] {
  return text.split("\n").filter(Boolean).map((line) => line.split(",").map((c) => c.trim().replace(/^["']|["']$/g, "")));
}

function detectColumns(headers: string[]): { dateIdx: number; descIdx: number; debitIdx: number; creditIdx: number; balIdx: number; refIdx: number } {
  const lower = headers.map((h) => h.toLowerCase());
  const find = (kw: string[]) => lower.findIndex((h) => kw.some((k) => h.includes(k)));
  return {
    dateIdx: Math.max(0, find(["date", "txn date", "value date", "posting date"])),
    descIdx: Math.max(1, find(["description", "narration", "particular", "remark"])),
    debitIdx: find(["debit", "withdrawal", "dr"]),
    creditIdx: find(["credit", "deposit", "cr"]),
    balIdx: find(["balance", "closing", "running"]),
    refIdx: find(["reference", "ref", "txn id", "utr", "cheque"]),
  };
}

function parseStatementRows(rows: string[][]): ImportedStatement[] {
  if (rows.length < 2) return [];
  const cols = detectColumns(rows[0]);
  const results: ImportedStatement[] = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;
    const dateStr = row[cols.dateIdx] || "";
    const desc = row[cols.descIdx] || "";
    const debitVal = cols.debitIdx >= 0 ? parseFloat(row[cols.debitIdx]?.replace(/[^\d.]/g, "") || "0") : 0;
    const creditVal = cols.creditIdx >= 0 ? parseFloat(row[cols.creditIdx]?.replace(/[^\d.]/g, "") || "0") : 0;
    const balVal = cols.balIdx >= 0 ? parseFloat(row[cols.balIdx]?.replace(/[^\d.]/g, "") || "0") : 0;
    const ref = cols.refIdx >= 0 ? (row[cols.refIdx] || "") : "";
    let amount = debitVal || creditVal;
    let type: "credit" | "debit" = creditVal > 0 ? "credit" : "debit";
    if (debitVal === 0 && creditVal === 0) {
      const amtIdx = rows[0].findIndex((h) => /amount/i.test(h));
      if (amtIdx >= 0) { amount = Math.abs(parseFloat(row[amtIdx]?.replace(/[^\d.-]/g, "") || "0")); type = amount >= 0 ? "credit" : "debit"; amount = Math.abs(amount); }
    }
    if (!dateStr || amount === 0) continue;
    const cat = categorizeTransaction(desc, type);
    results.push({
      id: `imp-${crypto.randomUUID().slice(0, 6)}`, date: dateStr, description: desc, reference: ref,
      debit: type === "debit" ? amount : 0, credit: type === "credit" ? amount : 0, balance: balVal, type,
      suggestedCategory: cat.category, suggestedDebitLedger: cat.debitLedger,
      suggestedCreditLedger: cat.creditLedger, suggestedVoucherType: cat.voucherType,
      confidence: cat.confidence, status: "pending",
    });
  }
  return results;
}

const DEMO_STATEMENT_CSV = `Date,Description,Reference,Debit,Credit,Balance
08/03/2026,NEFT/ABC Corp/INV-2026-045,NEFT260308001,,150000,2450000
07/03/2026,UPI/PhonePe/XYZ Solutions,UPI260307015,,45000,2300000
07/03/2026,IMPS/SALARY/MAR2026,IMPS260307022,450000,,2255000
06/03/2026,NEFT/Tech Park Realty/RENT,NEFT260306005,85000,,2705000
06/03/2026,UPI/GooglePay/CloudServices,UPI260306030,12500,,2790000
05/03/2026,RTGS/MegaCorp/PO-4521,RTGS260305002,,350000,2802500
05/03/2026,NEFT/GST Payment/Feb2026,NEFT260305011,67000,,2452500
04/03/2026,NACH/HDFC Life/Premium,NACH260303001,15000,,2321850
04/03/2026,NEFT/DEF Industries/ADV Payment,NEFT260304008,,200000,2521850
03/03/2026,Interest Credit/Q4,INT260301001,,3500,2336850
03/03/2026,NEFT/Purchase/Raw Material,NEFT260303009,125000,,2333350
02/03/2026,UPI/Swiggy/Food,UPI260302055,2350,,2458350
02/03/2026,UPI/Amazon/Office Supplies,UPI260302061,8499,,2460700
02/03/2026,UPI/HP Petrol Fuel Station,UPI260302078,3200,,2469199
01/03/2026,UPI/Uber/Ride to Airport,UPI260301022,850,,2472399
01/03/2026,NEFT/Transfer to SBI,TRF260301001,100000,,2460700`;

// ── Seed Data ────────────────────────────────────────────────────────────

const INDIAN_BANKS = [
  { name: "State Bank of India", code: "SBI", logo: "🏦" },
  { name: "HDFC Bank", code: "HDFC", logo: "🏛️" },
  { name: "ICICI Bank", code: "ICICI", logo: "🔷" },
  { name: "Axis Bank", code: "AXIS", logo: "🔶" },
  { name: "Kotak Mahindra Bank", code: "KOTAK", logo: "🔴" },
  { name: "Punjab National Bank", code: "PNB", logo: "🏪" },
  { name: "Bank of Baroda", code: "BOB", logo: "🟠" },
  { name: "Canara Bank", code: "CANARA", logo: "🔵" },
  { name: "Union Bank of India", code: "UNION", logo: "🟤" },
  { name: "Indian Bank", code: "INDIAN", logo: "🟢" },
  { name: "Bank of India", code: "BOI", logo: "⭐" },
  { name: "Yes Bank", code: "YES", logo: "🟡" },
];

const SEED_ACCOUNTS: BankAccount[] = [];
const SEED_TRANSACTIONS: BankTransaction[] = [];

// ── Daily chart data ─────────────────────────────────────────────────────
const DAILY_FLOW: { date: string; inflow: number; outflow: number }[] = [];

const RECON_PIE: { name: string; value: number; fill: string }[] = [];

// ── Connect Bank Dialog ──────────────────────────────────────────────────

function ConnectBankDialog({ open, onOpenChange, onConnect }: {
  open: boolean; onOpenChange: (o: boolean) => void;
  onConnect: (account: BankAccount) => void;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<"select" | "auth" | "confirm">("select");
  const [selectedBank, setSelectedBank] = useState<typeof INDIAN_BANKS[0] | null>(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountType, setAccountType] = useState<"current" | "savings">("current");
  const [connecting, setConnecting] = useState(false);

  const handleConnect = () => {
    if (!selectedBank || !accountNumber || !ifsc) return;
    setConnecting(true);
    setTimeout(() => {
      const account: BankAccount = {
        id: `ba-${crypto.randomUUID().slice(0, 6)}`,
        bankName: selectedBank.name,
        bankCode: selectedBank.code,
        accountNumber: `XXXX XXXX ${accountNumber.slice(-4)}`,
        accountType,
        ifscCode: ifsc.toUpperCase(),
        branch: "Auto-detected",
        holderName: "ABC Trading Co.",
        balance: Math.floor(Math.random() * 2000000) + 100000,
        lastSynced: new Date().toISOString(),
        status: "connected",
        logo: selectedBank.logo,
      };
      onConnect(account);
      setConnecting(false);
      toast({ title: "Bank Connected", description: `${selectedBank.name} account linked successfully` });
      onOpenChange(false);
      setStep("select");
      setSelectedBank(null);
      setAccountNumber("");
      setIfsc("");
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-accent" />
            Connect Bank Account
          </DialogTitle>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Select your bank to begin secure connection</p>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {INDIAN_BANKS.map((bank) => (
                <button
                  key={bank.code}
                  onClick={() => { setSelectedBank(bank); setStep("auth"); }}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-colors hover:border-accent hover:bg-accent/5 ${
                    selectedBank?.code === bank.code ? "border-accent bg-accent/10" : "border-border bg-card"
                  }`}
                >
                  <span className="text-xl">{bank.logo}</span>
                  <span className="text-sm font-medium text-foreground truncate">{bank.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "auth" && selectedBank && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <span className="text-2xl">{selectedBank.logo}</span>
              <div>
                <p className="text-sm font-medium text-foreground">{selectedBank.name}</p>
                <p className="text-xs text-muted-foreground">Secure bank connection via Account Aggregator</p>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-start gap-2">
              <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-primary">
                Your credentials are encrypted end-to-end and never stored on our servers. 
                Connection uses RBI-approved Account Aggregator framework.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Number</label>
                <Input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 18))}
                  placeholder="Enter account number"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">IFSC Code</label>
                <Input
                  value={ifsc}
                  onChange={(e) => setIfsc(e.target.value.toUpperCase().slice(0, 11))}
                  placeholder="e.g. HDFC0001234"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Type</label>
                <Select value={accountType} onValueChange={(v) => setAccountType(v as "current" | "savings")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current Account</SelectItem>
                    <SelectItem value="savings">Savings Account</SelectItem>
                    <SelectItem value="overdraft">Overdraft Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("select")}>Back</Button>
              <Button
                onClick={handleConnect}
                disabled={!accountNumber || !ifsc || connecting}
                className="gap-1.5"
              >
                {connecting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                {connecting ? "Connecting..." : "Connect Securely"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Match Transaction Dialog ─────────────────────────────────────────────

function MatchTransactionDialog({ transaction, open, onOpenChange, onMatch }: {
  transaction: BankTransaction; open: boolean; onOpenChange: (o: boolean) => void;
  onMatch: (txnId: string, voucherId: string, voucherNumber: string) => void;
}) {
  const { vouchers } = useAccounting();
  const [selectedVoucher, setSelectedVoucher] = useState("");

  const matchable = useMemo(() =>
    vouchers.filter((v) =>
      v.status === "Approved" &&
      Math.abs(v.totalDebit - transaction.amount) < 2
    ), [vouchers, transaction]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-accent" /> Match Transaction
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-bold text-foreground">₹{transaction.amount.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between mt-1"><span className="text-muted-foreground">Ref</span><span className="text-foreground">{transaction.reference}</span></div>
            <p className="text-xs text-muted-foreground mt-1">{transaction.description}</p>
          </div>

          {matchable.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Matching vouchers (by amount)</p>
              {matchable.map((v) => (
                <label key={v.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    selectedVoucher === v.id ? "border-accent bg-accent/5" : "border-border"
                  }`}
                >
                  <input type="radio" name="voucher" value={v.id} checked={selectedVoucher === v.id}
                    onChange={() => setSelectedVoucher(v.id)} className="sr-only" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-foreground">{v.voucherNumber}</span>
                    <span className="text-xs text-muted-foreground block">{v.partyName} • ₹{v.totalDebit.toLocaleString("en-IN")} • {v.date}</span>
                  </div>
                  {selectedVoucher === v.id && <CheckCircle2 className="h-4 w-4 text-accent" />}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No matching vouchers found for this amount</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!selectedVoucher} onClick={() => {
            const v = vouchers.find((v) => v.id === selectedVoucher);
            if (v) { onMatch(transaction.id, v.id, v.voucherNumber); onOpenChange(false); }
          }} className="gap-1.5"><CheckCircle2 className="h-4 w-4" /> Match</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────

const BankSyncPage = () => {
  const { role } = useAuth();
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<BankAccount[]>(SEED_ACCOUNTS);
  const [transactions, setTransactions] = useState<BankTransaction[]>(SEED_TRANSACTIONS);
  const [connectOpen, setConnectOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>("all");
  const [txnFilter, setTxnFilter] = useState<"all" | "credit" | "debit">("all");
  const [reconFilter, setReconFilter] = useState<"all" | "matched" | "unmatched" | "pending">("all");
  const [search, setSearch] = useState("");
  const [matchTxn, setMatchTxn] = useState<BankTransaction | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [importedStatements, setImportedStatements] = useState<ImportedStatement[]>([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredTxns = useMemo(() => {
    return transactions
      .filter((t) => selectedAccount === "all" || t.bankAccountId === selectedAccount)
      .filter((t) => txnFilter === "all" || t.type === txnFilter)
      .filter((t) => reconFilter === "all" || t.reconciliationStatus === reconFilter)
      .filter((t) =>
        t.description.toLowerCase().includes(search.toLowerCase()) ||
        t.reference.toLowerCase().includes(search.toLowerCase())
      );
  }, [transactions, selectedAccount, txnFilter, reconFilter, search]);

  const stats = useMemo(() => {
    const acctTxns = transactions.filter((t) => selectedAccount === "all" || t.bankAccountId === selectedAccount);
    const totalBalance = selectedAccount === "all"
      ? accounts.reduce((s, a) => s + a.balance, 0)
      : accounts.find((a) => a.id === selectedAccount)?.balance || 0;
    const credits = acctTxns.filter((t) => t.type === "credit").reduce((s, t) => s + t.amount, 0);
    const debits = acctTxns.filter((t) => t.type === "debit").reduce((s, t) => s + t.amount, 0);
    const unmatched = acctTxns.filter((t) => t.reconciliationStatus === "unmatched").length;
    const pending = acctTxns.filter((t) => t.reconciliationStatus === "pending").length;
    return { totalBalance, credits, debits, unmatched, pending };
  }, [accounts, transactions, selectedAccount]);

  const handleSync = useCallback((accountId: string) => {
    setSyncing(accountId);
    setAccounts((prev) => prev.map((a) => a.id === accountId ? { ...a, status: "syncing" } : a));
    setTimeout(() => {
      // Simulate new transactions
      const newTxn: BankTransaction = {
        id: `bt-${crypto.randomUUID().slice(0, 6)}`,
        bankAccountId: accountId,
        date: new Date().toISOString().split("T")[0],
        description: `NEFT/NewPayment/${Date.now().toString(36).toUpperCase()}`,
        reference: `NEFT${Date.now().toString().slice(-10)}`,
        type: Math.random() > 0.5 ? "credit" : "debit",
        amount: Math.floor(Math.random() * 100000) + 5000,
        balance: 0,
        reconciliationStatus: "unmatched",
      };
      setTransactions((prev) => [newTxn, ...prev]);
      setAccounts((prev) => prev.map((a) =>
        a.id === accountId ? { ...a, status: "connected", lastSynced: new Date().toISOString() } : a
      ));
      setSyncing(null);
      toast({ title: "Sync Complete", description: "Bank transactions updated" });
    }, 2500);
  }, [toast]);

  const handleMatchTransaction = useCallback((txnId: string, voucherId: string, voucherNumber: string) => {
    setTransactions((prev) => prev.map((t) =>
      t.id === txnId ? { ...t, reconciliationStatus: "matched" as const, matchedVoucherId: voucherId, matchedVoucherNumber: voucherNumber } : t
    ));
    toast({ title: "Matched", description: `Transaction matched with ${voucherNumber}` });
  }, [toast]);

  const handleIgnore = useCallback((txnId: string) => {
    setTransactions((prev) => prev.map((t) =>
      t.id === txnId ? { ...t, reconciliationStatus: "ignored" as const } : t
    ));
  }, []);

  const handleDisconnect = useCallback((accountId: string) => {
    setAccounts((prev) => prev.filter((a) => a.id !== accountId));
    setTransactions((prev) => prev.filter((t) => t.bankAccountId !== accountId));
    toast({ title: "Disconnected", description: "Bank account removed" });
  }, [toast]);

  // ── Statement Import Handlers ──────────────────────────────────────────

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const ext = file.name.split(".").pop()?.toLowerCase();

    if (ext === "csv" || ext === "txt") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const rows = parseCSVRows(text);
        const parsed = parseStatementRows(rows);
        setImportedStatements(parsed);
        setImporting(false);
        toast({ title: "Statement Imported", description: `${parsed.length} transactions extracted from ${file.name}` });
      };
      reader.readAsText(file);
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const parsed = parseStatementRows(rows);
        setImportedStatements(parsed);
        setImporting(false);
        toast({ title: "Statement Imported", description: `${parsed.length} transactions extracted from ${file.name}` });
      };
      reader.readAsArrayBuffer(file);
    } else if (ext === "pdf") {
      // PDF: show message to use CSV/Excel export from bank
      setImporting(false);
      toast({ title: "PDF Support", description: "For best results, export your bank statement as CSV or Excel. PDF parsing extracts limited data.", variant: "destructive" });
    } else {
      setImporting(false);
      toast({ title: "Unsupported Format", description: "Please upload CSV, Excel (.xlsx), or PDF files", variant: "destructive" });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [toast]);

  const handleLoadDemo = useCallback(() => {
    const rows = parseCSVRows(DEMO_STATEMENT_CSV);
    const parsed = parseStatementRows(rows);
    setImportedStatements(parsed);
    toast({ title: "Demo Loaded", description: `${parsed.length} sample transactions loaded` });
  }, [toast]);

  const handlePostEntry = useCallback((stmtId: string) => {
    setImportedStatements((prev) => prev.map((s) => s.id === stmtId ? { ...s, status: "posted" as const } : s));
    toast({ title: "Entry Posted", description: "Accounting entry created from bank transaction" });
  }, [toast]);

  const handlePostAll = useCallback(() => {
    const pending = importedStatements.filter((s) => s.status === "pending");
    setImportedStatements((prev) => prev.map((s) => s.status === "pending" ? { ...s, status: "posted" as const } : s));
    toast({ title: "All Entries Posted", description: `${pending.length} accounting entries created` });
  }, [importedStatements, toast]);

  const handleSkipEntry = useCallback((stmtId: string) => {
    setImportedStatements((prev) => prev.map((s) => s.id === stmtId ? { ...s, status: "skipped" as const } : s));
  }, []);

  const handleChangeCategory = useCallback((stmtId: string, newCategory: string) => {
    setImportedStatements((prev) => prev.map((s) => {
      if (s.id !== stmtId) return s;
      // Find matching rule for the new category to update ledger suggestions
      const rule = CATEGORY_RULES.find(r => r.category === newCategory);
      if (rule) {
        return { ...s, suggestedCategory: newCategory, suggestedDebitLedger: rule.debitLedger, suggestedCreditLedger: rule.creditLedger, suggestedVoucherType: rule.voucherType, confidence: "high" as const };
      }
      return { ...s, suggestedCategory: newCategory, confidence: "high" as const };
    }));
    toast({ title: "Category Updated", description: `Transaction re-categorized to "${newCategory}"` });
  }, [toast]);

  const importStats = useMemo(() => {
    const total = importedStatements.length;
    const pending = importedStatements.filter((s) => s.status === "pending").length;
    const posted = importedStatements.filter((s) => s.status === "posted").length;
    const highConf = importedStatements.filter((s) => s.confidence === "high").length;
    const totalCredits = importedStatements.filter((s) => s.type === "credit").reduce((sum, s) => sum + s.credit, 0);
    const totalDebits = importedStatements.filter((s) => s.type === "debit").reduce((sum, s) => sum + s.debit, 0);
    return { total, pending, posted, highConf, totalCredits, totalDebits };
  }, [importedStatements]);

  const reconStatusConfig: Record<string, { label: string; className: string }> = {
    matched: { label: "Matched", className: "text-success bg-success/10" },
    unmatched: { label: "Unmatched", className: "text-warning bg-warning/10" },
    pending: { label: "Pending", className: "text-muted-foreground bg-muted" },
    ignored: { label: "Ignored", className: "text-muted-foreground/60 bg-muted/50" },
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Bank Synchronization</h1>
          <p className="text-sm text-muted-foreground">Connect banks, auto-fetch transactions, and reconcile with your books</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => accounts.forEach((a) => handleSync(a.id))} className="gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync All
          </Button>
          <Button size="sm" onClick={() => setConnectOpen(true)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Connect Bank
          </Button>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((a) => (
          <div key={a.id} className={`bg-card border rounded-xl p-4 shadow-sm transition-colors ${
            selectedAccount === a.id ? "border-accent ring-1 ring-accent/20" : "border-border"
          }`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{a.logo}</span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{a.bankName}</p>
                  <p className="text-xs text-muted-foreground">{a.accountNumber} • {a.accountType}</p>
                </div>
              </div>
              <Badge className={`text-xs border-0 ${
                a.status === "connected" ? "text-success bg-success/10" :
                a.status === "syncing" ? "text-primary bg-primary/10" :
                a.status === "error" ? "text-destructive bg-destructive/10" :
                "text-muted-foreground bg-muted"
              }`}>
                {a.status === "connected" && <Wifi className="h-3 w-3 mr-1" />}
                {a.status === "syncing" && <RefreshCw className="h-3 w-3 mr-1 animate-spin" />}
                {a.status}
              </Badge>
            </div>

            <div className="mb-3">
              <p className="text-xs text-muted-foreground">Balance</p>
              <p className="text-xl font-bold text-foreground">₹{a.balance.toLocaleString("en-IN")}</p>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
              <span>IFSC: {a.ifscCode}</span>
              <span>{a.lastSynced ? `Synced ${new Date(a.lastSynced).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "Never synced"}</span>
            </div>

            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="flex-1 text-xs h-7 gap-1"
                onClick={() => setSelectedAccount(selectedAccount === a.id ? "all" : a.id)}>
                <Eye className="h-3 w-3" /> {selectedAccount === a.id ? "Show All" : "View"}
              </Button>
              <Button variant="outline" size="sm" className="text-xs h-7 gap-1"
                onClick={() => handleSync(a.id)} disabled={syncing === a.id}>
                <RefreshCw className={`h-3 w-3 ${syncing === a.id ? "animate-spin" : ""}`} /> Sync
              </Button>
              <Button variant="ghost" size="sm" className="text-xs h-7 w-7 p-0" title="Disconnect"
                onClick={() => handleDisconnect(a.id)}>
                <Unlink className="h-3 w-3 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
        ))}

        {accounts.length === 0 && (
          <div className="col-span-full text-center py-12 bg-card border border-dashed border-border rounded-xl">
            <Landmark className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No bank accounts connected</p>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Connect your bank to start auto-syncing transactions</p>
            <Button size="sm" onClick={() => setConnectOpen(true)} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Connect Bank
            </Button>
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Balance", value: `₹${stats.totalBalance.toLocaleString("en-IN")}`, icon: Landmark, color: "text-accent" },
          { label: "Incoming (Credits)", value: `₹${stats.credits.toLocaleString("en-IN")}`, icon: ArrowDownLeft, color: "text-success" },
          { label: "Outgoing (Debits)", value: `₹${stats.debits.toLocaleString("en-IN")}`, icon: ArrowUpRight, color: "text-destructive" },
          { label: "Unmatched", value: stats.unmatched, icon: AlertTriangle, color: "text-warning" },
          { label: "Pending Recon", value: stats.pending, icon: Clock, color: "text-muted-foreground" },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-1.5 mb-1">
              <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Charts + Transactions */}
      <Tabs defaultValue="transactions">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="transactions" className="text-sm gap-1.5"><CalendarDays className="h-3.5 w-3.5" />Transactions</TabsTrigger>
          <TabsTrigger value="import" className="text-sm gap-1.5"><Upload className="h-3.5 w-3.5" />Import Statement</TabsTrigger>
          <TabsTrigger value="analytics" className="text-sm gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2 flex-1 max-w-sm">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search transactions..." className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground" />
            </div>
            <div className="flex items-center gap-1">
              {(["all", "credit", "debit"] as const).map((f) => (
                <button key={f} onClick={() => setTxnFilter(f)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${txnFilter === f ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                  {f === "all" ? "All" : f === "credit" ? "↓ Credit" : "↑ Debit"}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {(["all", "matched", "unmatched", "pending"] as const).map((f) => (
                <button key={f} onClick={() => setReconFilter(f)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${reconFilter === f ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Transaction Table */}
          <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Description</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Reference</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxns.map((t) => {
                    const rc = reconStatusConfig[t.reconciliationStatus];
                    return (
                      <tr key={t.id} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-4 text-foreground">{new Date(t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                        <td className="py-3 px-4">
                          <span className="text-foreground text-sm">{t.description}</span>
                          {t.category && <Badge variant="outline" className="text-xs ml-2">{t.category}</Badge>}
                        </td>
                        <td className="py-3 px-4 text-xs text-muted-foreground font-mono">{t.reference}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-semibold ${t.type === "credit" ? "text-success" : "text-destructive"}`}>
                            {t.type === "credit" ? "+" : "-"}₹{t.amount.toLocaleString("en-IN")}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${rc.className}`}>
                            {t.reconciliationStatus === "matched" && <CheckCircle2 className="h-3 w-3" />}
                            {t.reconciliationStatus === "unmatched" && <AlertTriangle className="h-3 w-3" />}
                            {t.reconciliationStatus === "pending" && <Clock className="h-3 w-3" />}
                            {rc.label}
                          </span>
                          {t.matchedVoucherNumber && (
                            <span className="text-xs text-accent ml-1.5">{t.matchedVoucherNumber}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            {(t.reconciliationStatus === "unmatched" || t.reconciliationStatus === "pending") && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => setMatchTxn(t)}>
                                  <GitCompare className="h-3 w-3" /> Match
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Ignore" onClick={() => handleIgnore(t.id)}>
                                  <Unlink className="h-3 w-3 text-muted-foreground" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredTxns.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center">
                        <Landmark className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground font-medium">No transactions found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Cash Flow Chart */}
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-foreground mb-3">Daily Cash Flow (March 2026)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={DAILY_FLOW}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} className="text-muted-foreground" />
                  <Tooltip formatter={(v: number) => `₹${v.toLocaleString("en-IN")}`} />
                  <Area type="monotone" dataKey="inflow" stackId="1" stroke="hsl(var(--success))" fill="hsl(var(--success))" fillOpacity={0.2} name="Inflow" />
                  <Area type="monotone" dataKey="outflow" stackId="2" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive))" fillOpacity={0.2} name="Outflow" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Reconciliation Pie */}
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-foreground mb-3">Reconciliation Status</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={RECON_PIE} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {RECON_PIE.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${v} transactions`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {RECON_PIE.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.fill }} />
                      <span className="text-muted-foreground">{item.name}</span>
                    </div>
                    <span className="font-medium text-foreground">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── Import Statement Tab ──────────────────────────────────────── */}
        <TabsContent value="import" className="space-y-4 mt-4">
          {/* Upload Area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div
                className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-accent/50 transition-colors cursor-pointer bg-card"
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.txt" className="hidden" onChange={handleFileUpload} />
                <Upload className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">
                  {importing ? "Processing..." : "Upload Bank Statement"}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Drag & drop or click to upload. Supports CSV, Excel (.xlsx), and PDF
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Badge variant="outline" className="gap-1 text-xs"><FileText className="h-3 w-3" /> CSV</Badge>
                  <Badge variant="outline" className="gap-1 text-xs"><FileSpreadsheet className="h-3 w-3" /> Excel</Badge>
                  <Badge variant="outline" className="gap-1 text-xs"><FileText className="h-3 w-3" /> PDF</Badge>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Button variant="outline" className="w-full gap-1.5" onClick={handleLoadDemo}>
                <Zap className="h-4 w-4" /> Load Demo Statement
              </Button>
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-foreground">How it works</p>
                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2"><span className="bg-accent text-accent-foreground rounded-full h-4 w-4 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">1</span>Upload your bank statement</div>
                  <div className="flex items-start gap-2"><span className="bg-accent text-accent-foreground rounded-full h-4 w-4 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">2</span>System auto-extracts transactions</div>
                  <div className="flex items-start gap-2"><span className="bg-accent text-accent-foreground rounded-full h-4 w-4 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">3</span>AI suggests accounting entries</div>
                  <div className="flex items-start gap-2"><span className="bg-accent text-accent-foreground rounded-full h-4 w-4 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">4</span>Review & post to your books</div>
                </div>
              </div>
            </div>
          </div>

          {/* Import Results */}
          {importedStatements.length > 0 && (
            <>
              {/* Import Stats */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                  { label: "Total", value: importStats.total, icon: FileText, color: "text-accent" },
                  { label: "Pending", value: importStats.pending, icon: Clock, color: "text-warning" },
                  { label: "Posted", value: importStats.posted, icon: CheckCircle2, color: "text-success" },
                  { label: "High Confidence", value: importStats.highConf, icon: Zap, color: "text-primary" },
                  { label: "Credits", value: `₹${importStats.totalCredits.toLocaleString("en-IN")}`, icon: ArrowDownLeft, color: "text-success" },
                  { label: "Debits", value: `₹${importStats.totalDebits.toLocaleString("en-IN")}`, icon: ArrowUpRight, color: "text-destructive" },
                ].map((s) => (
                  <div key={s.label} className="bg-card border border-border rounded-lg p-2.5">
                    <div className="flex items-center gap-1 mb-0.5"><s.icon className={`h-3 w-3 ${s.color}`} /><span className="text-xs text-muted-foreground">{s.label}</span></div>
                    <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Action Bar */}
              {importStats.pending > 0 && (
                <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">{importStats.pending} entries ready to post</span>
                  </div>
                  <Button size="sm" onClick={handlePostAll} className="gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Post All Entries
                  </Button>
                </div>
              )}

              {/* Imported Transactions Table */}
              <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                        <th className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Description</th>
                        <th className="text-right py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Amount</th>
                        <th className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Category</th>
                        <th className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Suggested Entry</th>
                        <th className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Confidence</th>
                        <th className="text-right py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importedStatements.map((s) => {
                        const confConfig = {
                          high: { label: "High", className: "text-success bg-success/10" },
                          medium: { label: "Medium", className: "text-warning bg-warning/10" },
                          low: { label: "Low", className: "text-muted-foreground bg-muted" },
                        };
                        const cc = confConfig[s.confidence];
                        return (
                          <tr key={s.id} className={`border-t border-border/50 transition-colors ${
                            s.status === "posted" ? "bg-success/5 opacity-70" : s.status === "skipped" ? "bg-muted/30 opacity-50" : "hover:bg-muted/30"
                          }`}>
                            <td className="py-2.5 px-3 text-foreground text-xs">{s.date}</td>
                            <td className="py-2.5 px-3">
                              <span className="text-foreground text-xs block max-w-[200px] truncate" title={s.description}>{s.description}</span>
                              {s.reference && <span className="text-[10px] text-muted-foreground font-mono">{s.reference}</span>}
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              <span className={`text-xs font-semibold ${s.type === "credit" ? "text-success" : "text-destructive"}`}>
                                {s.type === "credit" ? "+" : "-"}₹{(s.credit || s.debit).toLocaleString("en-IN")}
                              </span>
                            </td>
                            <td className="py-2.5 px-3">
                              {s.status === "pending" ? (
                                <Select value={s.suggestedCategory} onValueChange={(v) => handleChangeCategory(s.id, v)}>
                                  <SelectTrigger className="h-7 w-[140px] text-[10px] border-dashed">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ALL_CATEGORIES.map((cat) => (
                                      <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">{s.suggestedCategory}</Badge>
                              )}
                            </td>
                            <td className="py-2.5 px-3">
                              <div className="text-[10px] text-muted-foreground space-y-0.5">
                                <div className="flex items-center gap-1">
                                  <span className="text-foreground font-medium">Dr:</span> {s.suggestedDebitLedger}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-foreground font-medium">Cr:</span> {s.suggestedCreditLedger}
                                </div>
                                <Badge className="text-[9px] h-4 px-1 border-0 bg-accent/10 text-accent">{s.suggestedVoucherType}</Badge>
                              </div>
                            </td>
                            <td className="py-2.5 px-3">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cc.className}`}>
                                {s.confidence === "high" && <Zap className="h-2.5 w-2.5" />}
                                {cc.label}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              {s.status === "pending" ? (
                                <div className="flex items-center justify-end gap-0.5">
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={() => handlePostEntry(s.id)}>
                                    <CheckCircle2 className="h-3 w-3 text-success" /> Post
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleSkipEntry(s.id)}>
                                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </div>
                              ) : (
                                <Badge className={`text-[10px] border-0 ${
                                  s.status === "posted" ? "text-success bg-success/10" : "text-muted-foreground bg-muted"
                                }`}>
                                  {s.status === "posted" ? "Posted" : "Skipped"}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {importedStatements.length === 0 && (
            <div className="text-center py-8">
              <FileSpreadsheet className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No statement imported yet</p>
              <p className="text-xs text-muted-foreground mt-1">Upload a bank statement or load demo data to get started</p>
            </div>
          )}
        </TabsContent>

      </Tabs>

      {/* Dialogs */}
      <ConnectBankDialog open={connectOpen} onOpenChange={setConnectOpen}
        onConnect={(a) => setAccounts((prev) => [...prev, a])} />
      {matchTxn && (
        <MatchTransactionDialog transaction={matchTxn} open={!!matchTxn}
          onOpenChange={(o) => !o && setMatchTxn(null)} onMatch={handleMatchTransaction} />
      )}
    </div>
  );
};

export default BankSyncPage;
